import { useEffect } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";
import { CallNowButton, EstimateButton } from "@/components/CTAButtons";
import { business, DEMO_PHONE_NOTICE } from "@/data/site";
import { track, trackPageView } from "@/lib/analytics";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Defer so the page's title/meta (set via useSeo) is applied before we log the view.
    const t = setTimeout(() => trackPageView(pathname), 50);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
};

// Reusable full-width CTA band used at the end of most pages.
export const CTABand = ({ heading = "Ready to Get Started?", sub = "Call now or request your free, no-obligation estimate. We're here to help New Yorkers 24/7." }) => (
  <section className="bg-[#059669] text-white" data-testid="cta-band">
    <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20 text-center">
      <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mb-4 tracking-tight">{heading}</h2>
      <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">{sub}</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a href={business.phoneHref} onClick={() => track("call_now_click", { location: "ctaband" })} className="inline-flex items-center justify-center gap-2 bg-white text-[#059669] font-bold uppercase tracking-wide px-8 py-4 text-lg border-2 border-white hover:bg-[#0F2E4C] hover:text-white hover:border-[#0F2E4C] transition-colors" data-testid="ctaband-call-btn">
          Call {business.phoneDisplay}
        </a>
        <EstimateButton size="lg" className="!bg-[#0F2E4C] !border-[#0F2E4C] hover:!bg-[#1A4A6B]" testid="ctaband-estimate-btn" />
      </div>
    </div>
  </section>
);

// Simple page hero for interior pages.
export const PageHero = ({ eyebrow, title, subtitle, testid = "page-hero" }) => (
  <section className="bg-[#0F2E4C] text-white" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
      {eyebrow && <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#34D399] mb-3">{eyebrow}</p>}
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4 max-w-4xl">{title}</h1>
      {subtitle && <p className="text-lg text-slate-300 max-w-2xl">{subtitle}</p>}
    </div>
  </section>
);

const Layout = () => {
  // Intercept every tel: link site-wide and show the demo notice instead of dialing.
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href^="tel:"]');
      if (anchor) {
        e.preventDefault();
        window.alert(DEMO_PHONE_NOTICE);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Toaster position="top-center" richColors />
      <Header />
      <main className="flex-1 pb-16 lg:pb-0">
        <Outlet />
      </main>
      <Footer />
      <MobileStickyCTA />
    </div>
  );
};

export default Layout;
export { CallNowButton, EstimateButton };
